import type {Table} from "@tanstack/react-table";
import {Check} from "lucide-react";

import {Button} from "@/components/ui/button";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,} from "@/components/ui/command";
import {Popover, PopoverContent, PopoverTrigger,} from "@/components/ui/popover";
import {cn} from "@/utils/shadcn-utils.js";
import * as React from "react";
import {UnfoldMore, ViewWeekOutlined} from "@mui/icons-material";

interface DataTableViewOptionsProps<TData> {
    table: Table<TData>;
    columnsAdjustmentHeight: number;
}

export function DataTableViewOptions<TData>({
                                                table,
                                                columnsAdjustmentHeight = 8
                                            }: DataTableViewOptionsProps<TData>) {
    const columns = React.useMemo(
        () =>
            table
                .getAllColumns()
                .filter(
                    (column) =>
                        typeof column.accessorFn !== "undefined" && column.getCanHide(),
                ),
        [table.options.columns],
    );

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    aria-label="Toggle columns"
                    role="combobox"
                    variant="outline"
                    size="sm"
                    className={`ml-auto hidden h-${columnsAdjustmentHeight} lg:flex ${columnsAdjustmentHeight !== 8 ? "text-md font-normal" : ""}`}
                >
                    <ViewWeekOutlined sx={{width: 18, height: 18}}/>
                    Ajuster les colonnes
                    <UnfoldMore sx={{width: 18, height: 18}} className="ml-auto opacity-50"/>
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[210px] p-0">
                <Command>
                    <CommandInput placeholder="Rechercher colonne..."/>
                    <CommandList>
                        <CommandEmpty>Aucune colonne</CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                key="all"
                                className={`${table.getIsAllColumnsVisible() ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                                onSelect={() =>
                                    table.toggleAllColumnsVisible(true)
                                }
                            >
                                Toutes
                                <Check
                                    className={cn(
                                        "ml-auto size-4 shrink-0",
                                        table.getIsAllColumnsVisible() ? "opacity-100" : "opacity-0",
                                    )}
                                />
                            </CommandItem>
                            {columns.filter(column => [true, undefined].includes(column.columnDef.meta?.visibleInColumnsAdjustment)).map((column) => (
                                <CommandItem
                                    key={column.id}
                                    className="cursor-pointer"
                                    onSelect={() =>
                                        column.toggleVisibility(!column.getIsVisible())
                                    }
                                >
                  <span title={column.columnDef.meta?.label} className="truncate">
                    {
                        (column.columnDef.meta?.labelInColumnsAdjustment || column.columnDef.meta?.label) ?? column.id
                    }
                  </span>
                                    <Check
                                        className={cn(
                                            "ml-auto size-4 shrink-0",
                                            column.getIsVisible() ? "opacity-100" : "opacity-0",
                                        )}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
